import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { PdfFooterSettings } from "@/features/settings/pages/material-compliance/supplier-inquiries/AttachmentTab"
import { DeadlinesSettings } from "@/features/settings/pages/material-compliance/supplier-inquiries/DeadlinesTab"
import { DefaultCcSettings } from "@/features/settings/pages/material-compliance/supplier-inquiries/EmailsTab"
import {
  EmailSignatureSection,
  SendingAddressSection,
} from "@/features/settings/pages/organization/EmailSendingPage"
import { settingsTargetTrailKeys } from "@/features/settings/settingsTargets"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useState, type ReactNode } from "react"

/** The org settings an inquiry draft takes values from, and can open. */
export type DraftSettingsTarget =
  | "email.signature"
  | "email.sendingAddress"
  | "inquiry.cc"
  | "inquiry.reminders"
  | "inquiry.footer"

const SHEETS: Record<
  DraftSettingsTarget,
  { titleKey: TranslationKeys; content: () => ReactNode }
> = {
  "email.signature": {
    titleKey: "SETTINGS/EMAIL_SENDING/SIGNATURE_TITLE",
    content: () => <EmailSignatureSection withPreview={false} />,
  },
  "email.sendingAddress": {
    titleKey: "SETTINGS/EMAIL_SENDING/ADDRESS_TITLE",
    content: () => <SendingAddressSection />,
  },
  "inquiry.cc": {
    titleKey: "MC_TPL_EC_CC_HEADING",
    content: () => <DefaultCcSettings />,
  },
  "inquiry.reminders": {
    titleKey: "SETTINGS/SUPPLIER_INQUIRIES/TAB_DEADLINES",
    content: () => <DeadlinesSettings />,
  },
  "inquiry.footer": {
    titleKey: "MC_TPL_FOOTER_LABEL",
    content: () => <PdfFooterSettings />,
  },
}

/**
 * One of the org's settings, opened beside the draft rather than a link away:
 * leaving would drop the draft's unsaved subject and body. Saved there, it
 * reaches the draft at once, through the same settings query.
 */
export function DraftSettingsSheet({
  target,
  onClose,
}: {
  target: DraftSettingsTarget | null
  onClose: () => void
}) {
  const { translate } = useTranslation()
  // Kept while the sheet slides out, so its content doesn't vanish first.
  const [shown, setShown] = useState(target)
  if (target && target !== shown) setShown(target)
  const sheet = shown ? SHEETS[shown] : null

  return (
    <Sheet open={!!target} onOpenChange={(open) => !open && onClose()}>
      {/* Content starts below the close button, which would otherwise sit
          on a section's EN/DE switch. */}
      <SheetContent className="flex w-full flex-col overflow-y-auto pt-12 sm:max-w-xl">
        {/* The section below shows the same title and explanation. */}
        <SheetHeader className="sr-only">
          <SheetTitle>{sheet && translate(sheet.titleKey)}</SheetTitle>
          <SheetDescription>
            {shown &&
              translate("SETTINGS/FROM_SETTINGS", {
                place: settingsTargetTrailKeys(shown)
                  .map((key) => translate(key))
                  .join(" › "),
              })}
          </SheetDescription>
        </SheetHeader>
        {sheet?.content()}
      </SheetContent>
    </Sheet>
  )
}
